import type { LiHTMLAttributes, ReactNode, Ref } from 'react';

import { cn } from '@shared/lib/cn';
import { Checkbox } from '@shared/ui/checkbox';
import { Icon } from '@shared/ui/icon';

import {
  type ListboxSelection,
  OPTION_ACTIVE_CLASS,
  OPTION_BASE_CLASS,
  OPTION_DISABLED_CLASS,
  OPTION_HOVER_CLASS,
  OPTION_SELECTED_CLASS,
} from './listbox.constant';

export interface ListboxOptionProps extends Omit<
  LiHTMLAttributes<HTMLLIElement>,
  'children' | 'onSelect'
> {
  /** Figma `Label` */
  children?: ReactNode;
  /** Figma `selection`. single = 선택되면 primary 채움 + 체크 아이콘, multiple = 앞에 체크 상자. 기본 single */
  selection?: ListboxSelection;
  /** Figma `selected` */
  selected?: boolean;
  /** Figma `state=disabled` */
  disabled?: boolean;
  /** 키보드로 가리킨 줄. hover 와 같은 모양을 그린다(`aria-activedescendant` 로 알린다) */
  active?: boolean;
  /** 누르면 부른다(disabled 이면 부르지 않는다) */
  onSelect?: () => void;
  ref?: Ref<HTMLLIElement>;
}

/**
 * 목록의 한 줄(`role="option"`). 버튼이 아니라 선택 항목이다 — 포커스는 목록(또는 Select 트리거)이 갖는다.
 * Figma `_Listbox/Option`.
 */
export function ListboxOption({
  children,
  selection = 'single',
  selected = false,
  disabled = false,
  active = false,
  onSelect,
  className,
  onClick,
  onMouseDown,
  ref,
  ...rest
}: ListboxOptionProps) {
  const key = selected ? 'on' : 'off';
  return (
    <li
      ref={ref}
      role="option"
      aria-selected={selected}
      aria-disabled={disabled || undefined}
      data-slot="listbox-option"
      data-selection={selection}
      data-active={active || undefined}
      {...rest}
      // 누를 때 포커스가 트리거에서 빠져나가지 않게 한다
      onMouseDown={(event) => {
        event.preventDefault();
        onMouseDown?.(event);
      }}
      onClick={(event) => {
        onClick?.(event);
        if (!disabled) onSelect?.();
      }}
      className={cn(
        OPTION_BASE_CLASS,
        disabled && OPTION_DISABLED_CLASS[selection][key],
        !disabled && active && OPTION_ACTIVE_CLASS[selection][key],
        !disabled && !active && OPTION_SELECTED_CLASS[selection][key],
        !disabled && 'cursor-pointer',
        !disabled && OPTION_HOVER_CLASS[selection][key],
        className,
      )}
    >
      {selection === 'multiple' ? (
        // 체크 상자는 모양만 — 누르기 · 포커스는 줄(option)이 받는다
        <span className="pointer-events-none flex min-w-0 flex-1">
          <Checkbox
            checked={selected}
            disabled={disabled}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className="min-w-0"
          >
            <span className="truncate">{children}</span>
          </Checkbox>
        </span>
      ) : (
        <>
          <span className="min-w-0 flex-1 truncate">{children}</span>
          {selected ? <Icon name="check" size="sm" /> : null}
        </>
      )}
    </li>
  );
}
